import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import PostagePanel from './PostagePanel';
import { shippingApi } from './api';

export default function PostageReconciliation({ orderId, capabilities, onAccessLost, readOnly = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [pending, setPending] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setData(null); setError(''); setPending(false);
    shippingApi.postage(orderId, controller.signal).then((value) => {
      if (!controller.signal.aborted) {
        if (value.order_id !== orderId) throw new Error('Expense preview belongs to a different order.');
        setData(value);
      }
    }).catch((err) => {
      if (controller.signal.aborted) return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else setError(err.message || 'Actual expenses could not be read.');
    });
    return () => controller.abort();
  }, [orderId, revision, onAccessLost]);
  const reload = () => { if (alive.current) setRevision((v) => v + 1); };
  return <Stack gap={2}>
    <Typography variant="h6">Actual postage · reconciliation preview</Typography>
    <Alert severity="info">This ledger does not automatically change Profits. Administrators can separately review eligible orders for Profit adoption. Consignment payouts are protected. Costs are allocated by purchased sale units, not card counts or SKU.</Alert>
    {error && <Alert severity="error">{error}</Alert>}
    {!data && !error && <CircularProgress size={24} aria-label="Reading actual postage" />}
    {data && <>
      {data.mode === 'REHEARSAL' && <Alert severity="info">Rehearsal expenses only</Alert>}
      <Typography>Order coverage: {data.coverage} · Known subtotal: {data.known_subtotal === null ? 'Unknown' : `AUD ${data.known_subtotal}`} · Complete actual cost: {data.actual_order_cost === null ? 'Not established' : `AUD ${data.actual_order_cost}`}</Typography>
      <Typography variant="body2">Buyer-paid shipping: {data.buyer_paid_shipping?.value == null ? 'Unknown' : `${data.buyer_paid_shipping.currency} ${data.buyer_paid_shipping.value}`} (order revenue, excluded from actual seller cost).</Typography>
      {data.coverage !== 'COMPLETE' && <Alert severity="warning">Partial or unknown costs cannot replace a whole-order shipping estimate.</Alert>}
      {data.issues.map((issue) => <Alert key={issue} severity="warning">{issue}</Alert>)}
      {!data.packages.length && <Typography>No local packages available for expense entry.</Typography>}
      {data.packages.map((value) => <PostagePanel key={`${orderId}-${value.shipment_id}-${revision}`} value={value}
        capabilities={readOnly ? { ...capabilities, postage: { ...capabilities.postage, can_record: false } } : capabilities}
        onSaved={reload} onPending={setPending} onAccessLost={onAccessLost} />)}
      {!!data.line_allocations.length && <Typography variant="subtitle2">Known cost allocation by source sale line</Typography>}
      {data.line_allocations.map((line) => <Typography key={line.source_order_item_id} variant="body2">Source row {line.source_order_item_id}: AUD {line.known_amount}</Typography>)}
    </>}
    <Button onClick={reload}>{pending ? 'Reload ledger to check request outcome' : 'Reload expense preview'}</Button>
  </Stack>;
}
